import { Bars3Icon, StarIcon, UserIcon } from '@heroicons/react/24/outline'
import React, { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useAppDispatch } from '@/hooks'
import { cn } from '@/lib/utils'
import { customerTabs, Sidebar } from '@/pages/customer/sidebar'
import { logout } from '@/slices/auth'

import { LogoIcon, LogoutIcon, UserTickIcon } from '@/assets/icons'

interface LayoutProps {
	title: string
	children: React.ReactNode
}

export const Layout = ({ title, children }: LayoutProps) => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const [isDialogOpen, setIsDialogOpen] = useState(false)
	return (
		<div className="p-5 min-h-screen relative max-w-[150rem] mx-auto bg-[#F6F6F680] mb-0 flex flex-col">
			<Sidebar isDialogOpen={isDialogOpen} setIsDialogOpen={setIsDialogOpen} />
			<div className="absolute max-lg:hidden w-[339px] inset-y-0 bottom-[37px] rounded-[32px] left-5 top-3 z-10 overflow-y-auto bg-white px-6 py-10">
				<div className="-m-1.5 p-1.5 flex items-center gap-3">
					<a href="/" className="-m-1.5 p-1.5">
						<span className="sr-only">Book Stands</span>
						<LogoIcon className="h-[69px] w-auto shrink-0" />
					</a>
					<div className="flex flex-col flex-wrap gap-3">
						<h3 className="text-secondary text-xl font-extrabold">minmini</h3>
						<p className="font-medium text-muted-foreground">
							(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
						</p>
					</div>
				</div>
				<div className="mt-3 h-px w-full bg-[#0B224D0D]" />
				<div className="mt-8 flow-root">
					<h4 className="uppercase text-muted-foreground">Menu</h4>
					<div className="space-y-3 pt-3">
						{customerTabs.map(tab => (
							<NavLink
								key={tab.name}
								to={tab.href}
								onClick={() => {
									if (tab.name === 'REGAL MIETEN') {
										window.location.href = '/customer/regal-mieten'
									}
								}}
								className={({ isActive }) =>
									cn(
										'flex items-center group hover:bg-secondary hover:text-white cursor-pointer gap-2 font-medium rounded-lg px-4 py-[13px]',
										isActive ? 'text-white bg-secondary' : 'text-muted-foreground',
									)
								}>
								{({ isActive }) => (
									<>
										<tab.icon
											className={cn(
												'group-hover:text-white',
												isActive ? 'text-white' : 'text-secondary/50',
											)}
										/>
										{tab.name}
									</>
								)}
							</NavLink>
						))}
					</div>
					<div
						onClick={() => dispatch(logout())}
						className="flex cursor-pointer items-center mt-10 uppercase gap-2 rounded-lg px-4 py-[13px] font-medium text-muted-foreground hover:bg-secondary hover:text-white">
						<LogoutIcon />
						Logout
					</div>
					<NavLink
						to="/customer/recruit"
						className={({ isActive }) =>
							cn(
								'flex cursor-pointer mt-3 items-center uppercase gap-2 rounded-lg px-4 py-[13px] font-medium text-muted-foreground hover:bg-secondary hover:text-white',
								isActive ? 'text-white bg-secondary' : 'text-muted-foreground',
							)
						}>
						<UserTickIcon />
						FREUNDE WERBEN FREUNDE
					</NavLink>
				</div>
			</div>

			<div className="rounded-[20px] lg:ml-[359px] pl-3 bg-white max-lg:p-4 lg:pb-6 lg:pt-6 lg:px-8 flex flex-col gap-[22px]">
				<div className="flex lg:hidden items-center justify-between">
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setIsDialogOpen(true)}
							className="size-8 flex items-center justify-center rounded-md border border-[#EEEFF0] text-secondary">
							<span className="sr-only">Open main menu</span>
							<Bars3Icon aria-hidden="true" className="size-6" />
						</button>
						<h2 className="text-secondary capitalize text-sm font-extrabold">{title}</h2>
					</div>
					<div className="flex items-center gap-2">
						<Link
							to="https://g.page/r/CXbzk_e_uMhPEAE/review"
							target="_blank"
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<StarIcon className="size-4 shrink-0" />
						</Link>
						<div
							onClick={() => navigate('/customer/profile')}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<UserIcon className="size-4 shrink-0" />
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between max-lg:hidden">
					<h1 className="text-secondary text-xl font-extrabold">{title}</h1>
					<div className="flex items-center gap-6">
						<div className="flex items-center gap-3">
							<Link to="https://g.page/r/CXbzk_e_uMhPEAE/review" target="_blank">
								<Button
									variant="outline"
									className="py-2 border border-[#EEEFF0] rounded-xl h-10 font-medium px-2.5">
									<StarIcon />
									Bewerte uns
								</Button>
							</Link>
							<Button
								variant="outline"
								size="icon"
								onClick={() => navigate('/customer/profile')}
								className="border border-[#EEEFF0] rounded-xl h-10 font-medium">
								<UserIcon className="size-5 shrink-0" />
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className="lg:ml-[359px] flex flex-col grow">{children}</div>
		</div>
	)
}
